import { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useClientTracking } from "@/hooks/useClientTracking";
import { PageTransition } from "@/components/PageTransition";
import { Preloader } from "@/components/Preloader";
import Index from "./pages/Index";
import Components from "./pages/Components";
import Projects from "./pages/Projects";
import Admin from "./pages/Admin";
import Favorites from "./pages/Favorites";
import Workspace from "./pages/Workspace";
import BOM from "./pages/BOM";
import Login from "./pages/Login";
import Register from "./pages/Register";
import NotFound from "./pages/NotFound";
import RefRedirect from "./pages/RefRedirect";
import CaseArticle from "./pages/CaseArticle";
import AdminCaseArticles from "./pages/AdminCaseArticles";
import AdminCaseArticleEditor from "./pages/AdminCaseArticleEditor";
import Cases from "./pages/Cases";

import AdminComponents from "./pages/AdminComponents";
import AdminComponentEditor from "./pages/AdminComponentEditor";
import OAuthCallback from "./pages/OAuthCallback";

function TrackedRoutes() {
  useClientTracking();
  return (
    <PageTransition>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/components" element={<Components />} />
        <Route path="/components/*" element={<Components />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/cases" element={<Cases />} />
        
        <Route path="/admin" element={<Admin />} />
        <Route path="/favorites" element={<Favorites />} />
        <Route path="/workspace/:projectId" element={<Workspace />} />
        <Route path="/bom/:projectId" element={<BOM />} />
        <Route path="/cases/:slug" element={<CaseArticle />} />
        <Route path="/admin/cases" element={<AdminCaseArticles />} />
        <Route path="/admin/cases/new" element={<AdminCaseArticleEditor />} />
        <Route path="/admin/cases/:id/edit" element={<AdminCaseArticleEditor />} />
        <Route path="/admin/components" element={<AdminComponents />} />
        <Route path="/admin/components/new" element={<AdminComponentEditor />} />
        <Route path="/admin/components/:id/edit" element={<AdminComponentEditor />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/~oauth" element={<OAuthCallback />} />
        <Route path="/:refCode" element={<RefRedirect />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </PageTransition>
  );
}


const queryClient = new QueryClient();

function preloadImages(urls: string[]): Promise<void[]> {
  return Promise.all(
    urls.map(
      (url) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Don't block on failed images
          img.src = url;
        })
    )
  );
}

const App = () => {
  const [user, setUser] = useState<{ email: string; name?: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [contentReady, setContentReady] = useState(false);

  useEffect(() => {
    // Only preload the 3 featured case images shown on the homepage
    supabase
      .from("case_articles")
      .select("hero_image_url")
      .eq("published", true)
      .is("deleted_at", null)
      .order("display_order", { ascending: true, nullsFirst: false })
      .limit(3)
      .then(({ data }) => {
        const urls = (data || [])
          .map((a) => a.hero_image_url)
          .filter(Boolean) as string[];
        if (urls.length === 0) {
          setContentReady(true);
          return;
        }
        preloadImages(urls).then(() => setContentReady(true));
      });
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.user) {
          setUser({
            email: session.user.email || "",
            name: session.user.user_metadata?.name,
          });
        } else {
          setUser(null);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser({
          email: session.user.email || "",
          name: session.user.user_metadata?.name,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        {isLoading && (
          <Preloader
            onComplete={() => setIsLoading(false)}
            isContentReady={contentReady}
          />
        )}
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <TrackedRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
